import React, { useEffect, useState } from "react";
import {
  CheckIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/20/solid";

import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import OrderItemsSubTable1 from "./items/OrderItemsSubTable1";
import Input from "../../controls/Input";
import { useCellSaveState } from "../../../hooks/useCellSaveState";

import {
  getOrderItemUnitPrice,
  getOrderItemUnitPriceField,
  isSunDistribution,
} from "../../../shared/utility";

function SaveStatusIndicator({ status }) {
  const saveStatusLabel = {
    saving: "Saving",
    saved: "Saved",
    error: "Save failed",
  }[status];

  const saveStatusIndicator = {
    saving: (
      <span
        className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-gray-300 border-t-indigo-600"
        aria-hidden="true"
      />
    ),
    saved: <CheckIcon className="h-5 w-5 text-green-800" aria-hidden="true" />,
    error: (
      <ExclamationCircleIcon
        className="h-4 w-4 text-red-600"
        aria-hidden="true"
      />
    ),
  }[status];

  return (
    <span
      className="inline-flex h-5 w-5 shrink-0 items-center justify-center"
      role={saveStatusLabel ? "status" : undefined}
      aria-label={saveStatusLabel}
      title={saveStatusLabel}
    >
      {saveStatusIndicator}
    </span>
  );
}

function priceToInputValue(unitPrice) {
  return unitPrice === null || unitPrice === undefined ? "" : unitPrice;
}

function getShippedLineTotal(rowData, isSun) {
  if (isSun) {
    if (
      rowData.sun_total_order_shipped_amount !== null &&
      rowData.sun_total_order_shipped_amount !== undefined
    ) {
      return rowData.sun_total_order_shipped_amount;
    }
    const shipped = parseFloat(rowData.shipped_amount);
    const unitPrice = parseFloat(rowData.sun_unit_price);
    if (Number.isNaN(shipped) || Number.isNaN(unitPrice)) {
      return "0.00";
    }
    return (shipped * unitPrice).toFixed(2);
  }

  if (rowData.total_order_shipped_amount === null) {
    return "0.00";
  }
  return rowData.total_order_shipped_amount;
}

// Local state is required: PrimeReact BodyCell is memoized on rowData only, so
// parent draft state never reaches the input while typing.
function UnitPriceCell({ rowData, onPersist }) {
  const unitPrice = getOrderItemUnitPrice(rowData);
  const [value, setValue] = useState(() => priceToInputValue(unitPrice));
  const { getSaveStatus, saveCell } = useCellSaveState();
  const saveStatus = getSaveStatus(String(rowData.id));

  useEffect(() => {
    setValue(priceToInputValue(getOrderItemUnitPrice(rowData)));
  }, [rowData.solmaz_unit_price, rowData.sun_unit_price]);

  return (
    <div
      className="flex items-center gap-2"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <Input
        labelOn={false}
        name={`unit_price_${rowData.id}`}
        type="number"
        step="any"
        className="block w-24 rounded-md border border-slate-300 px-2.5 py-1.5 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/30"
        value={value}
        onChange={(e) => {
          e.stopPropagation();
          setValue(e.target.value);
        }}
        onKeyDown={(e) => e.stopPropagation()}
        onBlur={(e) => onPersist(rowData, e.target.value, saveCell)}
      />
      <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center">
        {saveStatus !== "idle" ? (
          <SaveStatusIndicator status={saveStatus} />
        ) : null}
      </span>
    </div>
  );
}

export default function OrderItemsForShippingV2(props) {
  const {
    askDeleteShippingItem,
    order,
    openQuickScanPopupWithFetchLatest,
    rowDetails,
    fetchRowDetails,
    loadingRows,
    openSunShippingLabelReview,
    updateOrderItemUnitPrice,
    setOrderItems,
  } = props;

  const [expandedRows, setExpandedRows] = useState(null);
  const [isSunDistributionFlow, setIsSunDistributionFlow] = useState(false);

  const allowExpansion = (rowData) => {
    return true;
  };

  const onRowExpand = (event) => {
    const rowData = event.data;
    if (!rowDetails[rowData.id]) {
      fetchRowDetails(rowData);
    }
  };

  // Only collapse rows when the item set itself changes, not on every parent re-render.
  const orderItemIdsKey = (order.orderItems || []).map((item) => item.id).join(",");

  useEffect(() => {
    setExpandedRows(null);
  }, [orderItemIdsKey]);

  useEffect(() => {
    setIsSunDistributionFlow(isSunDistribution());
  }, []);

  const productDetailsBody = (rowData) => {
    return (
      <div className="py-0.5">
        <p className="font-semibold text-slate-900">{rowData.product_name}</p>
        <p className="text-xs text-slate-500">PLU: {rowData.plu}</p>
      </div>
    );
  };

  const orderQuantityBody = (rowData) => {
    return (
      <p className="text-sm text-slate-700">
        {rowData.quantity} {rowData.selling_unit_of_measure}
      </p>
    );
  };

  const shippedQuantity = (rowData) => {
    return (
      <p className="text-sm text-slate-700">
        {rowData.shipped_amount} {rowData.selling_unit_of_measure}
      </p>
    );
  };

  const persistUnitPrice = async (rowData, rawValue, saveCell) => {
    if (!updateOrderItemUnitPrice) {
      return;
    }

    const nextPrice =
      rawValue === "" || rawValue === null || rawValue === undefined
        ? 0
        : parseFloat(rawValue);

    if (Number.isNaN(nextPrice)) {
      return;
    }

    const currentRaw = getOrderItemUnitPrice(rowData);
    const currentPrice =
      currentRaw === null || currentRaw === undefined
        ? 0
        : parseFloat(currentRaw);

    if (currentPrice === nextPrice) {
      return;
    }

    const priceField = getOrderItemUnitPriceField();
    const key = String(rowData.id);
    await saveCell(key, async () => {
      const callResult = await updateOrderItemUnitPrice(rowData.id, nextPrice);
      if (callResult?.success) {
        const returnedData = callResult.data || {};
        const updatedOrderItems = (order.orderItems || []).map((item) => {
          if (item.id !== rowData.id) {
            return item;
          }

          const nextItem = {
            ...item,
            ...returnedData,
          };

          if (returnedData[priceField] === undefined) {
            nextItem[priceField] = nextPrice;
          }

          if (isSunDistributionFlow) {
            const shipped = parseFloat(
              returnedData.shipped_amount ?? item.shipped_amount
            );
            const sunPrice = parseFloat(
              returnedData.sun_unit_price !== undefined
                ? returnedData.sun_unit_price
                : nextPrice
            );
            if (!Number.isNaN(shipped) && !Number.isNaN(sunPrice)) {
              nextItem.sun_total_order_shipped_amount = (
                shipped * sunPrice
              ).toFixed(2);
            }
          }

          return nextItem;
        });
        setOrderItems(updatedOrderItems);
      }
      return { success: Boolean(callResult?.success) };
    });
  };

  const unitPriceBody = (rowData) => (
    <UnitPriceCell rowData={rowData} onPersist={persistUnitPrice} />
  );

  const totalPriceBody = (rowData) => {
    return (
      <p className="text-sm font-medium text-slate-900">
        ${getShippedLineTotal(rowData, isSunDistributionFlow)}
      </p>
    );
  };

  const rowExpansionTemplate = (data) => {
    return (
      <OrderItemsSubTable1
        askDeleteShippingItem={askDeleteShippingItem}
        data={data}
        loadingRows={loadingRows}
        rowDetails={rowDetails}
        openQuickScanPopupWithFetchLatest={openQuickScanPopupWithFetchLatest}
        openSunShippingLabelReview={openSunShippingLabelReview}
        isSunDistributionFlow={isSunDistributionFlow}
      />
    );
  };

  return (
    <div className="flow-root">
      <div className="overflow-x-auto rounded-xl border border-slate-200">
        <DataTable
          value={order.orderItems}
          expandedRows={expandedRows}
          onRowToggle={(e) => {
            setExpandedRows(e.data);
          }}
          onRowExpand={onRowExpand}
          rowExpansionTemplate={rowExpansionTemplate}
          dataKey="id"
          tableClassName="min-w-full bg-white text-sm wip-incoming-items-table"
          cellClassName="px-3 py-2.5 border-b border-slate-100 text-sm align-middle"
        >
          <Column
            header="Product"
            body={productDetailsBody}
            headerClassName="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 px-3 py-2.5"
            style={{ width: "40%" }}
          />
          <Column
            header="Order qty/Cs"
            body={orderQuantityBody}
            headerClassName="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 px-3 py-2.5"
            style={{ width: "15%" }}
          />
          <Column
            header="Shipped qty"
            body={shippedQuantity}
            headerClassName="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 px-3 py-2.5"
            style={{ width: "15%" }}
          />
          <Column
            header="Unit price"
            body={unitPriceBody}
            headerClassName="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 px-3 py-2.5"
            style={{ width: "15%" }}
          />
          <Column
            header="Total price"
            body={totalPriceBody}
            headerClassName="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 px-3 py-2.5"
            style={{ width: "15%" }}
          />
          <Column
            expander={allowExpansion}
            headerClassName="bg-slate-50 px-3 py-2.5"
            style={{ width: "5rem" }}
          />
        </DataTable>
      </div>
      {isSunDistributionFlow && (
        <div className="mt-5 flex justify-end">
          <div className="w-full max-w-sm rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-slate-600">
                  Subtotal
                </span>
                <span className="text-sm font-semibold text-slate-900">
                  ${order.shipped_subtotal_dollar_amount}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-slate-600">Tax</span>
                <span className="text-sm font-semibold text-slate-900">
                  $0.00
                </span>
              </div>
              <div className="mt-2 border-t border-slate-200 pt-2">
                <div className="flex items-center justify-between">
                  <span className="text-base font-bold text-slate-900">
                    Total
                  </span>
                  <span className="text-base font-bold text-slate-900">
                    ${order.shipped_total_dollar_amount}
                  </span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-base font-bold text-slate-900">
                  Total Cartons
                </span>
                <span className="text-base font-bold text-slate-900">
                  {order.total_shipped_cartons}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
